import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import api from '../services/api';
import { tenantStorage } from '../utils/storage';

const ANALYTICS_ENDPOINT = '/analytics/page-view'; // Relative to api baseURL

const generateUUID = () => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
        var r = Math.random() * 16 | 0, v = c == 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
    });
}

export const usePageTracking = () => {
    const location = useLocation();
    const lastPath = useRef<string | null>(null);

    useEffect(() => {
        // Exclude all subdomains (allow only root domains like example.com or localhost)
        // Checks if hostname has more than 2 parts (e.g. app.example.com = 3 parts -> exclude)
        // Exception: IPs are allowed (usually 4 parts but handled by regex if strictly needed, but simple split check works for names)
        // We assume standard TLDs (.com, .net, etc). 
        const hostname = window.location.hostname;
        const parts = hostname.split('.');

        // If it's an IP (4 parts numeric), we track it? User said "only something like blacksheepmission.net".
        // Let's allow IPs for testing, but block named subdomains.
        const isIp = /^\d+\.\d+\.\d+\.\d+$/.test(hostname);

        if (!isIp && parts.length > 2) {
            return;
        }

        // Visitor ID (persistent)
        let visitorId = tenantStorage.getItem('visitorId');
        if (!visitorId) {
            visitorId = generateUUID();
            tenantStorage.setItem('visitorId', visitorId);
        }

        // Session ID (session-based)
        let sessionId = sessionStorage.getItem('sessionId');
        if (!sessionId) {
            sessionId = generateUUID();
            sessionStorage.setItem('sessionId', sessionId);
        }

        const currentPath = location.pathname + location.search;

        // Avoid duplicate tracking (React Strict Mode double-invoke protection)
        if (lastPath.current === currentPath) {
            return;
        }
        lastPath.current = currentPath;

        const trackVisit = async () => {
            try {
                await api.post(ANALYTICS_ENDPOINT, {
                    url: window.location.href,
                    path: currentPath,
                    referrer: document.referrer,
                    sessionId,
                    visitorId,
                    // tenantId might need to be resolved from hostname or context if available
                });
            } catch (error) {
                console.error('Failed to track page view', error);
            }
        };

        trackVisit();
    }, [location]);
};
